import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  type IconDefinition
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';

const TOTAL_ITEMS_TO_SHOW = 5;
const MINIMUM_SUBTRACTION = (TOTAL_ITEMS_TO_SHOW - 1) / 2;

function pagesToShow(totalPages: number, currentPage: number) {
  if (totalPages <= TOTAL_ITEMS_TO_SHOW) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  let startingNumber = Math.max(1, currentPage - MINIMUM_SUBTRACTION);
  if (totalPages - currentPage < MINIMUM_SUBTRACTION) {
    let amountToSubtract = MINIMUM_SUBTRACTION + (totalPages - currentPage);
    if (totalPages === currentPage) amountToSubtract = 4;
    startingNumber = Math.max(1, currentPage - amountToSubtract);
  }

  return Array.from({ length: TOTAL_ITEMS_TO_SHOW }, (_, i) => i + startingNumber);
}

type PaginationItemProps = {
  active?: boolean;
  disableTabbing?: boolean;
  iconLeft?: IconDefinition;
  iconRight?: IconDefinition;
  isDisabled?: boolean;
  number?: number;
  text: string | number;
  onClick?: (e: React.MouseEvent, number: number) => void;
}

type PaginationProps = {
  currentPage: number;
  hideIfNoPagination?: boolean;
  showFirstLast?: boolean;
  showJumpTo?: boolean;
  showLabels?: boolean;
  totalPages: number;
  onChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  hideIfNoPagination = true,
  showFirstLast = true,
  showJumpTo = false,
  showLabels = true,
  totalPages,
  onChange
}: PaginationProps) {
  const [goToPage, setGoToPage] = useState('');
  const goToRef = useRef<NodeJS.Timeout | null>(null);

  if (hideIfNoPagination && !totalPages || totalPages <= 1) return null;

  function handleClick(event: React.MouseEvent, number: number) {
    event.preventDefault();
    onChange(number);
  }

  function handleGoToChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (goToRef.current) {
      clearTimeout(goToRef.current);
    }

    if (e.target.value === '') {
      setGoToPage(e.target.value);
      return;
    }

    const newValue = Number(e.target.value);

    if (isNaN(newValue)) {
      setGoToPage('');
      e.preventDefault();
      return;
    }

    setGoToPage(String(newValue));
    if (newValue === currentPage || newValue > totalPages) return;

    goToRef.current = setTimeout(() => {
      onChange(newValue);
    }, 1500);
  }

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const pagesShown = pagesToShow(totalPages, currentPage);

  const showStartingDots = pagesShown[0] !== 1;
  const showEndingDots = pagesShown[pagesShown.length - 1] !== totalPages;

  return (
    <ul className="pagination">
      {showFirstLast && (
        <PaginationItem
          iconLeft={faAngleDoubleLeft}
          isDisabled={isFirstPage}
          number={1}
          text={showLabels ? 'First' : ''}
          onClick={handleClick}
        />
      )}

      <PaginationItem
        iconLeft={faAngleLeft}
        isDisabled={isFirstPage}
        number={currentPage - 1}
        text={showLabels ? 'Previous' : ''}
        onClick={handleClick}
      />

      {showStartingDots && <PaginationItem isDisabled text="..."/>}

      {pagesShown.map((page) => {
        const isCurrentPage = page === currentPage;
        const key = `${page}-${isCurrentPage}`;
        return (
          <PaginationItem
            active={isCurrentPage}
            disableTabbing={isCurrentPage}
            key={key}
            number={page}
            text={page}
            onClick={handleClick}
          />
        );
      })}

      {showEndingDots && <PaginationItem isDisabled text="..."/>}

      <PaginationItem
        iconRight={faAngleRight}
        isDisabled={isLastPage}
        number={currentPage + 1}
        text={showLabels ? 'Next' : ''}
        onClick={handleClick}
      />

      {showFirstLast && (
        <PaginationItem
          iconRight={faAngleDoubleRight}
          isDisabled={isLastPage}
          number={totalPages}
          text={showLabels ? 'Last' : ''}
          onClick={handleClick}
        />
      )}

      {showJumpTo && (
        <li className="jump-to">
          Go to page
          <input className="form-control" type="number" value={goToPage} onChange={handleGoToChange}/>
        </li>
      )}
    </ul>
  );
}

function PaginationItem({
  active = false,
  disableTabbing = false,
  iconLeft,
  iconRight,
  isDisabled = false,
  number,
  text,
  onClick
}: PaginationItemProps) {
  const additionalProps = isDisabled || disableTabbing ? { tabIndex: -1 } : {};

  function handleClick(e: React.MouseEvent) {
    if (onClick && number) onClick(e, number);
  }

  return (
    <li className={classNames('page-item', { 'disabled': isDisabled, active })}>
      <a
        className="page-link"
        href="#"
        role="button"
        onClick={isDisabled ? undefined : handleClick}
        {...additionalProps}
      >
        {iconLeft ? <FontAwesomeIcon icon={iconLeft}/> : null}
        {text}
        {iconRight ? <FontAwesomeIcon icon={iconRight}/> : null}
      </a>
    </li>
  );
}
